import api from '@/services/api';
import { PaginatedResponse } from '@/types/common';
import {
  User,
  UserListItem,
  CreateUserRequest,
  CreateUserResponse,
  CreatePartnerViewerRequest,
  UpdateUserRequest,
  GetUsersParams,
  UserInviteLinkResponse,
} from '@/types/user';
import { partnerService } from '@/services/partner';
import { getAccessRole, getLinkedPartners, toApiUserRole } from '@/utils/rbac';
import type {
  BulkConfirmRequest,
  BulkConfirmResponse,
  BulkPreviewResponse,
  BulkUserType,
} from '@/types/bulkUsers';

type CurrentUserPayload = User & {
  partner?: { id?: string; partner_id?: string; name?: string };
};

const collectCurrentUserPartnerIds = (data: CurrentUserPayload): string[] => {
  const linked = getLinkedPartners(data).map((partner) => partner.id);
  const singular =
    data.partner && typeof data.partner === 'object'
      ? [data.partner.id, data.partner.partner_id]
      : [];
  const extra = singular
    .map((id) => String(id || '').trim())
    .filter((id): id is string => Boolean(id));
  const company = data.company_id?.trim();
  return [...new Set([...linked, ...extra, company].filter((id): id is string => Boolean(id)))];
};

const withPartnerFields = (data: CurrentUserPayload, ids: string[]): User => {
  const names = new Map(getLinkedPartners(data).map((partner) => [partner.id, partner.name]));
  if (data.partner && typeof data.partner === 'object') {
    const id = String(data.partner.id || data.partner.partner_id || '').trim();
    if (id) names.set(id, String(data.partner.name || names.get(id) || ''));
  }
  return {
    ...data,
    partner_id: ids[0],
    partner_ids: ids.length > 0 ? ids : undefined,
    partners: ids.map((id) => ({ id, name: names.get(id) || '' })),
  };
};

export const userService = {
  async getCurrentUser(): Promise<User> {
    const response = await api.get<CurrentUserPayload>('/users/me');
    const data = response.data;
    let ids = collectCurrentUserPartnerIds(data);
    const role = getAccessRole(data);
    if (ids.length === 0 && (role === 'CLIENT' || role === 'GUEST')) {
      try {
        const catalog = await partnerService.getAll({ offset: 0, limit: 1000 });
        ids = [...new Set((catalog.data || []).map((partner) => partner.id).filter(Boolean))];
      } catch {
        ids = [];
      }
    }
    return withPartnerFields(data, ids);
  },

  async getUsers(params: GetUsersParams = {}): Promise<PaginatedResponse<UserListItem>> {
    const queryParams = new URLSearchParams();

    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    
    if (params.q) {
      queryParams.append('q', params.q);
    }

    if (params.type) {
      queryParams.append('type', params.type);
    }

    if (params.role) {
      queryParams.append('role', toApiUserRole(params.role) || params.role);
    }

    params.roles?.forEach((role) => {
      const mapped = toApiUserRole(role) || role;
      queryParams.append('role', mapped);
    });

    if (params.status) {
      queryParams.append('status', params.status);
    }

    if (params.sort_by) {
      queryParams.append('sort_by', params.sort_by);
    }

    if (params.order) {
      queryParams.append('order', params.order);
    }

    params.partner_ids?.forEach((partnerId) => {
      queryParams.append('partner_ids', partnerId);
    });

    const url = `/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<PaginatedResponse<UserListItem>>(url);
    return response.data;
  },

  async getUserById(id: string): Promise<User> {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },

  async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await api.post<CreateUserResponse>('/users', userData);
    return response.data || {};
  },

  async getInviteLink(id: string): Promise<UserInviteLinkResponse> {
    const response = await api.get<UserInviteLinkResponse>(`/users/${id}/invite-link`);
    return response.data;
  },

  async createInviteLink(id: string): Promise<UserInviteLinkResponse> {
    const response = await api.post<UserInviteLinkResponse>(`/users/${id}/invite-link`);
    return response.data;
  },

  async createPartnerViewer(data: CreatePartnerViewerRequest): Promise<void> {
    await api.post('/users/partner-viewer', data);
  },

  async updateUser(id: string = '', userData: UpdateUserRequest): Promise<void> {
    await api.patch(`/users/${id}`, userData);
  },

  async blockUser(id: string): Promise<void> {
    await api.patch(`/users/${id}`, { status: 'BLOCKED' });
  },

  async unblockUser(id: string): Promise<void> {
    await api.patch(`/users/${id}`, { status: 'ACTIVE' });
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async previewBulkUsers(params: {
    file: File;
    userType: BulkUserType;
    brandIds: string[];
  }): Promise<BulkPreviewResponse> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('userType', params.userType);
    formData.append('partner_ids', params.brandIds.join(','));
    const response = await api.post<BulkPreviewResponse>('/users/bulk/preview', formData);
    return response.data;
  },

  async confirmBulkUsers(payload: BulkConfirmRequest): Promise<BulkConfirmResponse> {
    const response = await api.post<BulkConfirmResponse>('/users/bulk/confirm', payload);
    return response.data;
  },
}; 